import React, { useState, useEffect } from "react";
import { Button, Switch } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { grey } from "@mui/material/colors";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { usePermission } from "../../hooks/usePermission";

import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeTaxList,
} from "../../redux/counterSlice";

import Loader from "../loader/Loader";
import AddTax from "./AddTax";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import debounce from "lodash.debounce";
import { useCallback } from "react";

const Tax = () => {
  const dispatch = useDispatch();
  const methods = useForm();
  const { reset } = methods;
  const permissions = usePermission("Tax");

  const taxList = useSelector((state) => state.counter.taxList || []);
  const [loader, setLoader] = useState(false);
  const [activeTaxList, setActiveTaxList] = useState([]);
  const [open, setOpen] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("tax", 10);
  const tableHeight = useTableHeight();

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };
  const debouncedSearchChange = useCallback(
    debounce((value) => {
      onSearchChange(value);
    }, 300),
    []
  );

  const loadInitialData = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllTaxList");
      dispatch(storeTaxList(response.data?.data || []));
    } catch (err) {
      toast.error(err?.message || "Failed to fetch tax list");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const loadActiveTaxes = () => {
    setLoader(true);
    axios
      .get("/getAllActiveTaxList")
      .then((res) => setActiveTaxList(res?.data?.data || []))
      .catch((err) => toast.error(err?.message))
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    loadInitialData();
  }, [page, rowsPerPage]);

  const editClickHandler = (item) => {
    // loadActiveTaxes();
    setSelectedRow({ ...item });
    setOpen(true);
  };

  const toggleStatus = (tax_id, currentStatus) => {
    setLoader(true);
    const apiEndpoint =
      currentStatus === 1
        ? `/deActivateTax/${tax_id}`
        : `/activateTax/${tax_id}`;

    axios
      .get(apiEndpoint)
      .then(() => {
        toast.success(
          currentStatus === 1 ? "Tax deactivated" : "Tax activated"
        );
        loadInitialData();
      })
      .catch((err) => toast.error(err?.message || "Failed to toggle status"))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  const deleteClickHandler = async (id) => {
    try {
      const response = await axios.get(`/deleteTax/${id}`);
      if (response?.data?.error) {
        const errMsg = response?.data?.data
          ? Object.values(response.data.data).join("\n")
          : response?.data?.message || "Error occurred";
        dispatch(showModal({ msg: errMsg, error: true }));
      } else {
        toast.success(response?.data?.message || "Deleted successfully");
        loadInitialData();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showModal({ msg: error?.message || "Server Error", error: true })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const filteredTaxList = taxList.filter((tax) =>
    tax.tax_percentage
      .toString()
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{+dataIndex + 1}</p>,
        filter: false,
      },
    },
    {
      name: "tax_percentage",
      label: "Tax Percentage",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const tax = filteredTaxList[dataIndex];
          return <span>{tax?.tax_percentage}&nbsp;%</span>;
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const tax = filteredTaxList[dataIndex];
          const status = tax?.active_status;
          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(tax.tax_id, status),
                  })
                )
              }
              style={{ marginBottom: "5px" }}
            />
          );
        },
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
      },
    },
    
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const tax = filteredTaxList[dataIndex];
                const status = tax?.active_status;

                return (
                  <div className="actionBtns">
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1}
                        onClick={() => editClickHandler(tax)}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{
                            color: status === 1 ? grey[600] : "secondary.main",
                          }}
                        />
                      </Button>
                    )}

                    {permissions?.delete && (
                      <Button
                        disabled={status === 1}
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () => deleteClickHandler(tax.tax_id),
                            })
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          fontSize="medium"
                          sx={{
                            color: status === 1 ? grey[600] : "red",
                          }}
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Tax</h4>
          <MUIDataTable
            data={filteredTaxList}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    loadActiveTaxes();
                    setSelectedRow(null);
                    setOpen(true);
                    reset();
                  }}
                  startIcon={<AddIcon />}
                >
                  New Tax
                </Button>
              ) : null
            }
          />
        </>
      )}
      <AddTax
        open={open}
        handleClose={handleClose}
        selectedRow={selectedRow}
        loadInitialData={loadInitialData}
      />
    </>
  );
};

export default Tax;
